import React, { useState, useRef, useEffect } from "react";
import {
  Today, CalendarToday, Home, Book, BarChart, CreditCard,
  ChevronLeft, ChevronRight,
  Chat,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";



import logo from "../../../assets/app/logo.png";
import { RootState } from "../../../redux/store";
import ShortRequests from "./ShortRequests";
import Wallet from "../../../PAYMENTMETHODS/Wallet";
import ShortAccomodationCalendar from "./ShortAccomodationCalendar";
import ShortListings from "./ShortListings";
import ShortReservations from "./ShortReservations";
import ShortToday from "./ShortToday";
import IMessagePreview from "../../../SUPERAPP/MESSAGING/IMessagePreview";

/* ── design tokens — pure white, black/white, gold on hover ── */
const T = {
  bg:            "#FFFFFF",
  surface:       "#FFFFFF",
  border:        "#EBEBEB",
  border2:       "#DDDDDD",
  text:          "#0A0A0A",
  textSoft:      "#444444",
  muted:         "#9A9A9A",
  black:         "#0A0A0A",
  white:         "#FFFFFF",
  gold:          "#C9A84C",
  goldDark:      "#9A7A2E",
  goldSubtle:    "rgba(201,168,76,0.10)",
  goldBorder:    "rgba(201,168,76,0.35)",
  sidebar:       "#FFFFFF",
  sidebarHover:  "rgba(201,168,76,0.08)",
  sidebarActive: "#0A0A0A",
  sidebarBorder: "#EBEBEB",
  sans:          "'Montserrat', sans-serif",
  serif:         "'Cormorant Garamond', serif",
};

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&family=Montserrat:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; }

  /* ── sidebar visibility ── */
  .desk-sidebar { display: flex !important; }

  /* ── mobile tabs: always visible, no toggle ── */
  .mob-tabs-bar { display: none; }

  @media (max-width: 767px) {
    .mob-tabs-bar { display: block; }
    .desk-sidebar { display: none !important; }
    .desk-main    { margin-left: 0 !important; }
    .desk-crumb   { display: none !important; }
    .mob-crumb    { display: flex !important; }
  }

  /* ── scrollable pill row ── */
  .tabs-scroll {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 10px 14px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    background: #ffffff;
  }
  .tabs-scroll::-webkit-scrollbar { display: none; }

  /* ── pill styles ── */
  .tab-pill {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 8px 14px;
    border-radius: 20px;
    border: 1.5px solid #EBEBEB;
    background: #FFFFFF;
    cursor: pointer;
    flex-shrink: 0;
    transition: all .18s ease;
    font-family: 'Montserrat', sans-serif;
    min-width: 62px;
  }
  .tab-pill:hover {
    border-color: #C9A84C;
    background: rgba(201,168,76,0.08);
  }
  .tab-pill.active {
    background: #0A0A0A;
    border-color: #0A0A0A;
    box-shadow: 0 4px 14px rgba(0,0,0,0.18);
  }
  .tab-pill-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0A0A0A;
    transition: color .15s;
  }
  .tab-pill:hover .tab-pill-icon { color: #C9A84C; }
  .tab-pill.active .tab-pill-icon { color: #FFFFFF; }

  .tab-pill-label {
    font-size: 10px;
    font-weight: 500;
    color: #444444;
    white-space: nowrap;
    letter-spacing: 0.02em;
    transition: color .15s;
  }
  .tab-pill:hover .tab-pill-label { color: #9A7A2E; }
  .tab-pill.active .tab-pill-label { color: #FFFFFF; }

  /* ── mobile crumb ── */
  .mob-crumb {
    display: none;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 16px;
    background: #ffffff;
    border-bottom: 0.5px solid #EBEBEB;
  }

  /* ── sidebar nav scrollbar hide ── */
  .sidebar-nav::-webkit-scrollbar { display: none; }

  /* ── sidebar nav button hover/active ── */
  .nav-btn {
    position: relative;
    transition: background .18s ease, transform .15s ease;
  }
  .nav-btn:hover {
    background: rgba(201,168,76,0.08) !important;
  }
  .nav-btn:hover .nav-icon { color: #C9A84C !important; }
  .nav-btn:hover .nav-label { color: #9A7A2E !important; }

  .nav-btn.active {
    background: #0A0A0A !important;
    box-shadow: 0 4px 14px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.08);
  }
  .nav-btn.active .nav-icon  { color: #C9A84C !important; }
  .nav-btn.active .nav-label { color: #FFFFFF !important; }
  .nav-btn.active:hover { background: #0A0A0A !important; }
  .nav-btn.active:hover .nav-icon { color: #E8C96A !important; }
`;

const SIDEBAR_FULL = 220;
const SIDEBAR_MINI = 56;
const HEADER_H     = 56;

type Tab = { group: string; title: string; icon: React.ReactNode; key: string };

const TABS: Tab[] = [
  { group: "Overview", title: "Chat", key: "chat", icon: <Chat fontSize="small" /> },

  { group: "Overview",   title: "Today",        key: "today",        icon: <Today fontSize="small" /> },
  { group: "Overview",   title: "Calendar",     key: "calendar",     icon: <CalendarToday fontSize="small" /> },
  { group: "Properties", title: "Requests",     key: "requests",     icon: <Book fontSize="small" /> },
  { group: "Properties", title: "Listings",     key: "listings",     icon: <Home fontSize="small" /> },
  { group: "Properties", title: "Reservations", key: "reservations", icon: <Book fontSize="small" /> },
  { group: "Finance",    title: "Analytics",    key: "analytics",    icon: <BarChart fontSize="small" /> },
  { group: "Finance",    title: "Payouts",      key: "payouts",      icon: <CreditCard fontSize="small" /> },
];

const ShortDashboard: React.FC = () => {
  /* ── default active tab = "today" ── */
  const [active,    setActive]    = useState("today");
  const [collapsed, setCollapsed] = useState(false);
  const activeTabRef = useRef<HTMLButtonElement>(null);

  const userId   = useSelector((s: RootState) => s.auth.user?.userId);
  const navigate = useNavigate();

  const groups      = Array.from(new Set(TABS.map(t => t.group)));
  const sbW         = collapsed ? SIDEBAR_MINI : SIDEBAR_FULL;
  const activeTitle = TABS.find(t => t.key === active)?.title ?? "";

  /* scroll active pill into view on mobile */
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [active]);

  const renderContent = () => {
    switch (active) {
           case "chat":        return <IMessagePreview />;
      case "today":        return <ShortToday />;
      case "calendar":     return <ShortAccomodationCalendar />;
      case "requests":     return <ShortRequests />;
      case "listings":     return <ShortListings />;
      case "reservations": return <ShortReservations />;
      //case "analytics":    return <ShortRevenue />;
      case "payouts":      return <Wallet />;
          
      default:             return <ShortToday />;
    }
  };

  return (
    <div style={{
      display: "flex", flexDirection: "column", minHeight: "100vh",
      background: T.bg, fontFamily: T.sans, color: T.text,
    }}>
      <style>{FONT_CSS}</style>

      {/* ── HEADER ── */}
      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, height: HEADER_H,
        background: "#FFFFFF",
        borderBottom: `1px solid ${T.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 20px", zIndex: 300,
        boxShadow: "0 1px 0 #EBEBEB",
      }}>
        {/* logo + wordmark */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={logo} alt="Logo" style={{ height: 28, objectFit: "contain" }} />
          <span style={{
            color: T.text, fontSize: 13, fontWeight: 300,
            letterSpacing: "0.3em", fontFamily: T.serif,
          }}>
            ARELUXE
          </span>
        </div>

        {/* badge */}
        <div style={{
          background: T.black,
          border: `1px solid ${T.black}`,
          borderRadius: 20, padding: "4px 14px",
          boxShadow: "0 3px 10px rgba(0,0,0,0.18)",
        }}>
          <span style={{ fontSize: 11, color: T.gold, fontWeight: 500, letterSpacing: "0.03em" }}>Host dashboard</span>
        </div>
      </header>

      {/* ── MOBILE TABS BAR ── */}
      <div
        className="mob-tabs-bar"
        style={{
          position: "fixed", top: HEADER_H, left: 0, right: 0,
          background: T.surface,
          borderBottom: `1px solid ${T.border}`,
          zIndex: 200,
        }}
      >
        <div className="tabs-scroll">
          {TABS.map(({ key, title, icon }) => (
            <button
              key={key}
              ref={active === key ? activeTabRef : null}
              className={`tab-pill${active === key ? " active" : ""}`}
              onClick={() => setActive(key)}
            >
              <span className="tab-pill-icon">{icon}</span>
              <span className="tab-pill-label">{title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ display: "flex", paddingTop: HEADER_H, flex: 1 }}>

        {/* ── DESKTOP SIDEBAR ── */}
        <aside
          className="desk-sidebar"
          style={{
            position: "fixed", top: HEADER_H, left: 0, bottom: 0,
            width: sbW,
            background: T.sidebar,
            borderRight: `1px solid ${T.sidebarBorder}`,
            flexDirection: "column",
            transition: "width .25s ease",
            zIndex: 200, overflow: "hidden",
          }}
        >
          {/* listing info box */}
          {!collapsed && (
            <div style={{ padding: "10px 12px", borderBottom: `1px solid ${T.sidebarBorder}` }}>
              <div style={{
                background: T.black,
                border: `1px solid ${T.black}`,
                borderRadius: 8, padding: "10px 12px",
                boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
              }}>
                <p style={{
                  fontSize: 9, color: T.gold,
                  letterSpacing: "0.12em", textTransform: "uppercase",
                  margin: 0, fontWeight: 600,
                }}>
                  Host dashboard
                </p>
                <p style={{ fontSize: 11, color: T.white, marginTop: 4, marginBottom: 0, fontWeight: 500 }}>
                  My properties
                </p>
                <p style={{ fontSize: 9, color: T.gold, marginTop: 3, marginBottom: 0, display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: T.gold, display: "inline-block" }} />
                  Live
                </p>
              </div>
            </div>
          )}

          {/* nav */}
          <nav
            className="sidebar-nav"
            style={{ flex: 1, overflowY: "auto", padding: 8, scrollbarWidth: "none" }}
          >
            {groups.map(group => (
              <div key={group}>
                {!collapsed
                  ? (
                    <p style={{
                      fontSize: 9, color: T.muted,
                      letterSpacing: "0.13em", textTransform: "uppercase",
                      padding: "10px 8px 4px", margin: 0,
                    }}>
                      {group}
                    </p>
                  )
                  : <div style={{ height: 10 }} />
                }

                {TABS.filter(t => t.group === group).map(({ key, title, icon }) => {
                  const isActive = active === key;
                  return (
                    <button
                      key={key}
                      className={`nav-btn${isActive ? " active" : ""}`}
                      onClick={() => setActive(key)}
                      title={collapsed ? title : undefined}
                      style={{
                        display: "flex", alignItems: "center",
                        gap: collapsed ? 0 : 10,
                        justifyContent: collapsed ? "center" : "flex-start",
                        width: "100%",
                        padding: collapsed ? "10px 0" : "9px 8px",
                        borderRadius: 7, border: "none", cursor: "pointer",
                        background: "transparent",
                        fontFamily: T.sans, marginBottom: 2,
                      }}
                    >
                      {/* icon — black default, gold on hover/active */}
                      <span className="nav-icon" style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: isActive ? T.gold : T.black,
                        flexShrink: 0, width: 18, height: 18,
                        transition: "color .15s",
                      }}>
                        {icon}
                      </span>

                      {!collapsed && (
                        <span className="nav-label" style={{
                          fontSize: 12, flex: 1, whiteSpace: "nowrap", textAlign: "left",
                          fontWeight: isActive ? 500 : 400,
                          color: isActive ? T.white : T.textSoft,
                          transition: "color .15s",
                        }}>
                          {title}
                        </span>
                      )}

                      {/* active indicator dot — gold */}
                      {!collapsed && isActive && (
                        <span style={{
                          width: 5, height: 5, borderRadius: "50%",
                          background: T.gold, flexShrink: 0,
                          boxShadow: `0 0 6px ${T.gold}`,
                        }} />
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* collapse toggle */}
          <div style={{ borderTop: `1px solid ${T.sidebarBorder}`, padding: 8 }}>
            <button
              onClick={() => setCollapsed(p => !p)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: "100%", padding: 8, borderRadius: 7,
                border: "none", background: "transparent", cursor: "pointer",
                gap: collapsed ? 0 : 6, transition: "background .15s, color .15s",
                color: T.black,
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = T.sidebarHover;
                (e.currentTarget as HTMLElement).style.color = T.goldDark;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = "transparent";
                (e.currentTarget as HTMLElement).style.color = T.black;
              }}
            >
              {collapsed
                ? <ChevronRight style={{ fontSize: 16, color: "inherit" }} />
                : (
                  <>
                    <ChevronLeft style={{ fontSize: 16, color: "inherit" }} />
                    <span style={{ fontSize: 11, color: "inherit" }}>Collapse</span>
                  </>
                )
              }
            </button>
          </div>
        </aside>

        {/* ── MAIN CONTENT ── */}
        <main
          className="desk-main"
          style={{
            flex: 1,
            marginLeft: sbW,
            transition: "margin-left .25s ease",
            minWidth: 0,
            background: T.white,
          }}
        >
          {/* desktop breadcrumb */}
          <div
            className="desk-crumb"
            style={{
              height: 44,
              background: T.surface,
              borderBottom: `0.5px solid ${T.border}`,
              display: "flex", alignItems: "center",
              gap: 6, padding: "0 24px",
            }}
          >
            <span style={{ fontSize: 11, color: T.muted }}>Dashboard</span>
            <span style={{ fontSize: 11, color: T.border2 }}>/</span>
            <span style={{
              fontSize: 11, color: T.goldDark, fontWeight: 600,
            }}>
              {activeTitle}
            </span>
          </div>

          {/* mobile breadcrumb */}
          <div className="mob-crumb" style={{ marginTop: 66 }}>
            <span style={{ fontSize: 11, color: T.muted }}>Dashboard</span>
            <span style={{ fontSize: 11, color: T.border2 }}>/</span>
            <span style={{ fontSize: 11, color: T.goldDark, fontWeight: 600 }}>{activeTitle}</span>
          </div>

          <div style={{ padding: "20px 16px 100px" }}>
            {renderContent()}
          </div>
        </main>

      </div>
    </div>
  );
};

export default ShortDashboard;